import type { ApiService } from './api/ApiService';
import type { TableData } from '../types/api';
import type { ContentStoreState, ContentQuery } from '../stores/contentStore';
import {
  createContentGetRequest,
  createContentUpdateRequest,
  createContentListRequest,
  createContentSyncRequest,
  getServiceTypeForRequest,
  parseServiceResponse,
  SYNC_REQUEST_TYPES
} from './serviceHelpers';
import { BaseServiceClient } from './BaseServiceClient';

/**
 * Content sync service that manages content TableData records
 * Integrates with content store and backend API service
 */
export class contentService extends BaseServiceClient {
  constructor(
    apiService: ApiService,
    private contentStore: ContentStoreState
  ) {
    super(apiService);
  }

  /**
   * Get content from local store or fetch from backend if not available/outdated
   */
  async getContent(
    contentType: string,
    contentTypeId: string,
    languageTypeId: string,
    options: {
      forceRefresh?: boolean;
      maxCacheAgeMinutes?: number;
    } = {}
  ): Promise<TableData | null> {
    const { forceRefresh = false, maxCacheAgeMinutes = 60 } = options;
    const query: ContentQuery = { contentType, contentTypeId, languageTypeId };

    // Check if we should use cached content
    if (!forceRefresh && this.contentStore.hasContent(query)) {
      const isOutdated = this.contentStore.isContentOutdated(query, maxCacheAgeMinutes);
      
      if (!isOutdated) {
        const content = this.contentStore.getContent(query);
        return content?.tableData || null;
      }
    }

    // Debug: Check what content is actually in the store if AdminSegment lookup fails
    if (contentType === 'AdminSegment') {
      const allRecords = this.contentStore.getContentStats();
      console.log(`⚠️ AdminSegment ${contentTypeId} not found in bundled content`);
      console.log('📋 Available content records:', allRecords.totalRecords);
      
      const adminSegments = this.contentStore.getContentsByType('AdminSegment');
      console.log('📋 Available AdminSegments:', adminSegments.map(r => r.id));
      
      if (adminSegments.length === 0) {
        console.error('❌ No AdminSegments found in content store - bundled content may not be loaded');
      }
    }

    // Fetch from backend
    try {
      this.contentStore.setLoading(true);
      this.contentStore.setSyncError(null);

      // Debug: Check current auth token before making request
      const currentAuthToken = this.apiService.getAuthToken();
      console.log('🔐 contentService: Current auth token before request:', currentAuthToken ? '[SET]' : '[NOT SET]');
      if (currentAuthToken) {
        console.log('🔐 contentService: Token length:', currentAuthToken.length);
        console.log('🔐 contentService: Token start:', currentAuthToken.substring(0, 20) + '...');
      }

      const serviceRequest = createContentGetRequest(contentType, contentTypeId, languageTypeId);
      const serviceType = getServiceTypeForRequest(SYNC_REQUEST_TYPES.CONTENT_GET);
      
      console.log('🔄 contentService: Making service request for', contentType, contentTypeId);
      const response = await this.callServiceRequest(serviceRequest, serviceType);
      const tablesDto = parseServiceResponse<any>(response.payload);
      
      // Extract the first table from the TablesDto response
      if (!tablesDto.Tables || !Array.isArray(tablesDto.Tables) || tablesDto.Tables.length === 0) {
        console.error('contentService: No tables found in response', tablesDto);
        return null;
      }
      
      const tableData = tablesDto.Tables[0]; // Take the first table

      // Update local store
      this.contentStore.addOrUpdateContent(query, tableData);

      return tableData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Special handling for authentication errors
      if (errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        console.error('🔒 contentService: Authentication error detected');
        console.log('   Content requested:', contentType, contentTypeId);
        console.log('   Current auth token available:', !!this.apiService.getAuthToken());
        console.log('   Error details:', errorMessage);
        
        // Check if this might be a premium content access issue
        if (contentTypeId === '11270') {
          console.log('🏆 This is premium AdminSegment - user might not have premium access or token might be expired');
        }
        
        this.contentStore.setSyncError(`Authentication failed: Premium content may require re-authentication`);
      } else {
        this.contentStore.setSyncError(`Failed to fetch content: ${errorMessage}`);
      }
      
      // Try to return cached content as fallback
      const cachedContent = this.contentStore.getContent(query);
      return cachedContent?.tableData || null;
    } finally {
      this.contentStore.setLoading(false);
    }
  }

  /**
   * Update content in backend and local store
   */
  async updateContent(
    contentType: string,
    contentTypeId: string,
    languageTypeId: string,
    tableData: TableData
  ): Promise<boolean> {
    const query: ContentQuery = { contentType, contentTypeId, languageTypeId };

    try {
      this.contentStore.setLoading(true);
      this.contentStore.setSyncError(null);

      const serviceRequest = createContentUpdateRequest(contentType, contentTypeId, languageTypeId, tableData);
      const serviceType = getServiceTypeForRequest(SYNC_REQUEST_TYPES.CONTENT_UPDATE);
      
      await this.callServiceRequest(serviceRequest, serviceType);

      // Update local store with the new data
      this.contentStore.addOrUpdateContent(query, tableData);

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      this.contentStore.setSyncError(`Failed to update content: ${errorMessage}`);
      return false;
    } finally {
      this.contentStore.setLoading(false);
    }
  }

  /**
   * List all content of a specific type
   */
  async listContent(
    contentType: string,
    languageTypeId?: string
  ): Promise<TableData[]> {
    try {
      this.contentStore.setLoading(true);
      this.contentStore.setSyncError(null);

      const serviceRequest = createContentListRequest(contentType, languageTypeId);
      const serviceType = getServiceTypeForRequest(SYNC_REQUEST_TYPES.CONTENT_LIST);
      
      const response = await this.callServiceRequest(serviceRequest, serviceType);
      const tableDataList = parseServiceResponse<TableData[]>(response.payload);

      // Update local store with all the fetched content
      tableDataList.forEach(tableData => {
        // Assume the table data includes metadata to identify content
        // You may need to adjust this based on your actual table structure
        const query: ContentQuery = {
          contentType,
          contentTypeId: tableData.Name, // Using table name as ID, adjust as needed
          languageTypeId: languageTypeId || '1', // Default to English if not specified
        };
        this.contentStore.addOrUpdateContent(query, tableData);
      });

      return tableDataList;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      this.contentStore.setSyncError(`Failed to list content: ${errorMessage}`);
      return [];
    } finally {
      this.contentStore.setLoading(false);
    }
  }

  /**
   * Sync content types from backend (batch operation)
   */
  async syncContent(
    contentTypes: string[],
    languageTypeId: string,
    forceSync: boolean = false
  ): Promise<boolean> {
    try {
      this.contentStore.setSyncing(true);
      this.contentStore.setSyncError(null);

      const serviceRequest = createContentSyncRequest(contentTypes, languageTypeId, forceSync);
      const serviceType = getServiceTypeForRequest(SYNC_REQUEST_TYPES.CONTENT_SYNC);
      
      const response = await this.callServiceRequest(serviceRequest, serviceType);
      const syncResult = parseServiceResponse<{ 
        success: boolean; 
        contentData: Array<{
          contentType: string;
          contentTypeId: string;
          tableData: TableData;
        }>;
      }>(response.payload);

      if (syncResult.success && syncResult.contentData) {
        // Update local store with all synced content
        syncResult.contentData.forEach(({ contentType, contentTypeId, tableData }) => {
          const query: ContentQuery = {
            contentType,
            contentTypeId,
            languageTypeId,
          };
          this.contentStore.addOrUpdateContent(query, tableData);
        });
      }

      this.contentStore.markGlobalSyncComplete();
      return syncResult.success;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      this.contentStore.setSyncError(`Failed to sync content: ${errorMessage}`);
      return false;
    } finally {
      this.contentStore.setSyncing(false);
    }
  }

  /**
   * Get content statistics
   */
  getContentStats() {
    return this.contentStore.getContentStats();
  }

  /**
   * Clear specific content from local store
   */
  clearContent(contentType: string, contentTypeId: string, languageTypeId: string) {
    const query: ContentQuery = { contentType, contentTypeId, languageTypeId };
    this.contentStore.removeContent(query);
  }

  /**
   * Clear all content of a specific type
   */
  clearContentByType(contentType: string) {
    this.contentStore.removeContentsByType(contentType);
  }

  /**
   * Clear all cached content
   */
  clearAllContent() {
    this.contentStore.clearAllContent();
  }

  /**
   * Check if content is available locally
   */
  hasContent(contentType: string, contentTypeId: string, languageTypeId: string): boolean {
    const query: ContentQuery = { contentType, contentTypeId, languageTypeId };
    return this.contentStore.hasContent(query);
  }

  /**
   * Check if content is outdated
   */
  isContentOutdated(
    contentType: string,
    contentTypeId: string,
    languageTypeId: string,
    maxAgeMinutes: number = 60
  ): boolean {
    const query: ContentQuery = { contentType, contentTypeId, languageTypeId };
    return this.contentStore.isContentOutdated(query, maxAgeMinutes);
  }

  /**
   * Get local content without making API calls
   */
  getLocalContent(
    contentType: string,
    contentTypeId: string,
    languageTypeId: string
  ): TableData | null {
    const query: ContentQuery = { contentType, contentTypeId, languageTypeId };
    const content = this.contentStore.getContent(query);
    return content?.tableData || null;
  }
}

/**
 * Factory function to create contentService
 */
export function createcontentService(
  apiService: ApiService,
  contentStore: ContentStoreState
): contentService {
  return new contentService(apiService, contentStore);
}